import { BrowserRouter, Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";
import Search from "@app/page/search";
import { PageSpinner } from "@app/design-system/components";
import { DropoffTrackingProvider } from "@app/shared/context/DropoffTrackingContext";
import { HomeRoute } from "@app/features/home";
import { DesktopTrackingShell } from "./DesktopTrackingShell";

const OrderDetailsPage = lazy(() => import("@app/features/order-details").then((m) => ({ default: m.OrderDetailsPage })));
const StopDetailsPage = lazy(() => import("@app/features/stop-details").then((m) => ({ default: m.StopDetailsPage })));
const CustomerCustomFieldsPage = lazy(() =>
  import("@app/features/customer-custom-fields").then((m) => ({ default: m.CustomerCustomFieldsPage }))
);
const DriverCustomFieldsPage = lazy(() =>
  import("@app/features/driver-custom-fields").then((m) => ({ default: m.DriverCustomFieldsPage }))
);
const ServiceDetailsPage = lazy(() =>
  import("@app/features/service-details").then((m) => ({ default: m.ServiceDetailsPage }))
);
const VehicleDetailsPage = lazy(() =>
  import("@app/features/vehicle-details").then((m) => ({ default: m.VehicleDetailsPage }))
);
const EventHistoryPage = lazy(() => import("@app/features/event-history").then((m) => ({ default: m.EventHistoryPage })));
const NotFoundPage = lazy(() => import("@app/features/errors").then((m) => ({ default: m.NotFoundPage })));
const ExpirePage = lazy(() => import("@app/features/errors").then((m) => ({ default: m.ExpirePage })));

/**
 * Every page below Order Details, relative to whichever `/details/:trackCode`
 * (or `/d/details/:trackCode`) prefix mounts it — this tree has no idea
 * which tracking mode it's in, that's entirely `DropoffTrackingContext`'s
 * job (each page reads `useIsDropoffTracking()` itself). Split out so it
 * can be mounted twice (standard + dropoff-tracking) without repeating each
 * `<Route>` by hand — mirrors the pre-migration sandbox's `OrderRoutes`.
 */
const DetailsRoutes = () => (
  <Suspense fallback={<PageSpinner />}>
    <Routes>
      <Route index element={<OrderDetailsPage />} />
      <Route path="pickup" element={<StopDetailsPage kind="pickup" />} />
      <Route path="dropoff/:stopId" element={<StopDetailsPage kind="dropoff" />} />
      <Route path="customer/custom-fields" element={<CustomerCustomFieldsPage />} />
      <Route path="driver/custom-fields" element={<DriverCustomFieldsPage />} />
      <Route path="service/details" element={<ServiceDetailsPage />} />
      <Route path="vehicle/details" element={<VehicleDetailsPage />} />
      <Route path="events" element={<EventHistoryPage />} />
    </Routes>
  </Suspense>
);

export const RouteManager = () => {
  return (
    <BrowserRouter>
      <DesktopTrackingShell>
        <Routes>
          <Route path="/" element={<Search />} />
          <Route path="/:trackCode" element={<HomeRoute />} />
          <Route path="/details/:trackCode/*" element={<DetailsRoutes />} />
          <Route
            path="/d/:trackCode"
            element={
              <DropoffTrackingProvider>
                <HomeRoute />
              </DropoffTrackingProvider>
            }
          />
          <Route
            path="/d/details/:trackCode/*"
            element={
              <DropoffTrackingProvider>
                <DetailsRoutes />
              </DropoffTrackingProvider>
            }
          />
          <Route
            path="/not-found"
            element={
              <Suspense fallback={<PageSpinner />}>
                <NotFoundPage />
              </Suspense>
            }
          />
          <Route
            path="/expire"
            element={
              <Suspense fallback={<PageSpinner />}>
                <ExpirePage />
              </Suspense>
            }
          />
          <Route
            path="*"
            element={
              <Suspense fallback={<PageSpinner />}>
                <NotFoundPage />
              </Suspense>
            }
          />
        </Routes>
      </DesktopTrackingShell>
    </BrowserRouter>
  );
};
